'use client'

import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Pagination } from 'swiper/modules'
import { AGILUS_AWARDS_DATA } from '@/helper/DATA'
import { useMediaQuery } from '@/hooks/use-media-query'

interface AwardItem {
  img: any
  year: string
  text: string
}

const AwardCard = ({ award }: { award: AwardItem }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 flex flex-col items-center text-center shadow-sm h-full">
    <div className="w-[100px] h-[100px] md:w-[120px] md:h-[120px] flex items-center justify-center mb-4">
      <Image
        src={award.img}
        alt="Award Logo"
        className="object-contain max-w-full max-h-full"
      />
    </div>
    <span className="inline-block text-label-sm font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full mb-3">
      {award.year}
    </span>
    <p className="text-body-sm text-gray-800 leading-relaxed">{award.text}</p>
  </div>
)

const AwardsCarousel = ({ awards }: { awards?: AwardItem[] }) => {
  // ?? only substitutes for nullish, but AwardsAndRecognitionSection forwards
  // anything with a truthy .length - a non-array would reach .map below.
  const data =
    Array.isArray(awards) && awards.length > 0 ? awards : AGILUS_AWARDS_DATA
  const { mounted } = useMediaQuery('(min-width: 768px)')

  return (
    <>
      {/* Mobile */}
      <div className="md:hidden">
        {/* SSR + pre-hydration: static scrollable row so content is in the HTML */}
        {!mounted && (
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
            {data.map((award, index) => (
              <div key={index} className="snap-start shrink-0 w-[85%]">
                <AwardCard award={award} />
              </div>
            ))}
          </div>
        )}
        {/* Post-hydration: full Swiper carousel */}
        {mounted && (
          <Swiper
            loop
            pagination
            slidesPerView={1.15}
            spaceBetween={12}
            className="navigation_hidden testimonials_slider"
            modules={[Autoplay, Pagination]}
            autoplay={{ delay: 4000, disableOnInteraction: false }}
          >
            {data.map((award, index) => (
              <SwiperSlide key={index} className="h_auto">
                <AwardCard award={award} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>

      {/* Desktop: Grid layout — always SSR */}
      <div className="hidden md:grid md:grid-cols-4 gap-6">
        {data.map((award, index) => (
          <AwardCard key={index} award={award} />
        ))}
      </div>
    </>
  )
}

export default AwardsCarousel
